import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { type PlanStatusKind, planStatusKind, planStatusTone } from "./plan";

// The card's frame: hairlines between cards on phones, a raised card from
// 640px.
export const SETTINGS_CARD =
  "border-v3-border border-t pt-5 font-v3 text-v3-text first:border-t-0 first:pt-0 sm:rounded-v3-panel sm:border-t-0 sm:bg-v3-panel sm:p-6 sm:shadow-v3-hairline sm:first:pt-6";

// One v3.2 settings card (the Security and Transcription cards): an icon
// tile, a title with an optional chip beside it, a description, then the
// controls. On phones the cards are separated by hairlines inside the
// section panel; from 640px each is its own raised card.
export const SettingsCard = ({
  icon,
  title,
  description,
  aside,
  children,
  labelledBy,
  testId,
  tone = "default",
}: {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  aside?: ReactNode;
  children?: ReactNode;
  labelledBy: string;
  testId?: string;
  // "danger": the icon tile in the record red (Delete account).
  tone?: "default" | "danger";
}): JSX.Element => (
  <section
    aria-labelledby={labelledBy}
    className={SETTINGS_CARD}
    data-testid={testId}
  >
    <div className="flex items-start gap-3 sm:gap-4">
      <div
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-full shadow-v3-hairline sm:size-10 [&_svg]:size-[18px]",
          tone === "danger"
            ? "bg-v3-rec-tint text-v3-rec-text"
            : "bg-v3-surface-2 text-v3-text-2",
        )}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h3
            className="font-v3-display text-[16px] font-semibold leading-snug"
            id={labelledBy}
          >
            {title}
          </h3>
          {aside}
        </div>
        {description && (
          <p className="mt-1 text-[14px] leading-relaxed text-v3-text-2">
            {description}
          </p>
        )}
      </div>
    </div>
    {children && <div className="mt-4 empty:hidden sm:ml-14">{children}</div>}
  </section>
);

// A field label inside a settings card ("Default style").
export const SETTINGS_FIELD_LABEL =
  "mb-1.5 block font-v3 text-[13px] font-semibold text-v3-text-2";
// The hint under a field.
export const SETTINGS_FIELD_HINT =
  "mt-1.5 font-v3 text-[12.5px] leading-snug text-v3-text-3";

const TONE = {
  ok: "bg-v3-ok-tint text-v3-ok-text",
  warn: "bg-v3-warn-tint text-v3-warn-text",
  neutral: "bg-v3-surface-2 text-v3-text-2 shadow-v3-hairline",
} as const;

// The plan status as a quiet v3.2 chip ("Active", "Payment due"...).
export const PlanStatusChip = ({
  status,
  className,
}: {
  status?: string | null;
  className?: string;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const kind: PlanStatusKind = planStatusKind(status);
  if (kind === "unknown" && !status) return null;
  const label =
    kind === "unknown"
      ? (status ?? "")
      : t(`settings.subscription.v3.status.${kind}`);
  return (
    <span
      className={cn(
        "inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 font-v3 text-[12px] font-semibold",
        TONE[planStatusTone(kind)],
        className,
      )}
      data-status={kind}
      data-testid="plan-status-chip"
    >
      {planStatusTone(kind) === "ok" && (
        <span aria-hidden className="size-1.5 rounded-full bg-v3-ok" />
      )}
      {label}
    </span>
  );
};
